import { colId } from '../../constants/constants';

/**
 * Fusion de cellules et groupes de colonnes d'un tableau composé à la main.
 *
 * Module partagé document ↔ site : pur, sans React ni store. Le modèle vit dans
 * les **clés** — `mergedCells: [{ rowKeys, columnKeys }]`,
 * `columnsDisposition: [{ key, title, children }]` — et tout est recalculé au
 * rendu : une fusion survit au réordonnancement et au redimensionnement, et
 * celle dont une ligne ou une colonne a disparu est simplement ignorée plutôt
 * que de casser le tableau.
 */

// Modèle : mergedCells = [{ rowKeys: [...], columnKeys: [...] }].
// L'ancre (cellule rendue) est la case haut-gauche du rectangle dans l'ordre
// COURANT des lignes/colonnes ; les autres cases sont masquées (display:none).
// Tout est recalculé au rendu à partir des clés → survit au reorder / resize,
// et une fusion dont une clé a disparu (ligne/colonne supprimée) ou qui n'est
// plus un rectangle contigu est simplement ignorée.

const cellId = (rowKey, columnKey) => `${rowKey} ${columnKey}`;

/** Indices contigus ? (max - min + 1 === length, sans doublon) */
const isContiguous = (indices) => {
  if (indices.length === 0) return false;
  const sorted = [...indices].sort((a, b) => a - b);
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i] === sorted[i - 1]) return false;
  }
  return sorted[sorted.length - 1] - sorted[0] + 1 === sorted.length;
};

/**
 * Calcule le layout de fusion pour un rendu donné.
 * @returns {{ spanByCell: Map<string,{rowSpan,colSpan}>, coveredCells: Set<string> }}
 *   spanByCell : ancres → span à appliquer. coveredCells : cases à masquer.
 */
export const computeMergedCellLayout = (data, columns, mergedCells) => {
  const spanByCell = new Map();
  const coveredCells = new Set();
  if (!Array.isArray(mergedCells) || mergedCells.length === 0) {
    return { spanByCell, coveredCells };
  }
  const rowIndexByKey = new Map(data.map((row, idx) => [row.key, idx]));
  const colIndexByKey = new Map(columns.map((col, idx) => [col.key, idx]));

  mergedCells.forEach((merge) => {
    const rowKeys = merge?.rowKeys ?? [];
    const columnKeys = merge?.columnKeys ?? [];
    if (rowKeys.length === 0 || columnKeys.length === 0) return;
    if (rowKeys.length * columnKeys.length < 2) return;

    const rowIdxs = rowKeys.map((k) => rowIndexByKey.get(k));
    const colIdxs = columnKeys.map((k) => colIndexByKey.get(k));
    // Une clé absente (ligne/colonne supprimée) => fusion invalide, ignorée.
    if (rowIdxs.some((i) => i === undefined) || colIdxs.some((i) => i === undefined)) return;
    // Doit rester un rectangle contigu dans l'ordre courant.
    if (!isContiguous(rowIdxs) || !isContiguous(colIdxs)) return;

    const anchorRowIdx = Math.min(...rowIdxs);
    const anchorColIdx = Math.min(...colIdxs);
    const anchorRowKey = data[anchorRowIdx].key;
    const anchorColKey = columns[anchorColIdx].key;

    rowKeys.forEach((rk) => {
      columnKeys.forEach((ck) => {
        if (rk === anchorRowKey && ck === anchorColKey) return;
        coveredCells.add(cellId(rk, ck));
      });
    });
    // `contentKey` : colonne dont la cellule remplit l'ancre quand celle-ci est
    // vide (cf. computeLabelRowMerges). Ignorée si elle n'est pas dans la fusion.
    const contentColumnKey = columnKeys.includes(merge.contentKey) && merge.contentKey !== anchorColKey
      ? merge.contentKey
      : undefined;
    spanByCell.set(cellId(anchorRowKey, anchorColKey), {
      rowSpan: rowKeys.length,
      colSpan: columnKeys.length,
      ...(contentColumnKey ? { contentColumnKey } : {}),
    });
  });

  return { spanByCell, coveredCells };
};

export const mergedCellSpanKey = cellId;

/**
 * Lignes que représente la poignée d'une ligne. Une fusion de plusieurs lignes
 * qui atteint la dernière colonne ne laisse qu'une poignée, celle de sa cellule
 * fusionnée : elle agit sur toutes ses lignes (sélection, suppression,
 * déplacement). Sinon, la ligne seule. Clés dans l'ordre du tableau.
 */
export const rowGroupKeys = (data, columns, mergedCells, rowKey) => {
  const lastColumnKey = columns[columns.length - 1]?.key;
  const rowIndexByKey = new Map(data.map((row, idx) => [row.key, idx]));
  const colIndexByKey = new Map(columns.map((col, idx) => [col.key, idx]));
  for (const merge of mergedCells ?? []) {
    const rowKeys = merge?.rowKeys ?? [];
    const columnKeys = merge?.columnKeys ?? [];
    if (rowKeys.length < 2 || !rowKeys.includes(rowKey) || !columnKeys.includes(lastColumnKey)) continue;
    const rowIdxs = rowKeys.map((k) => rowIndexByKey.get(k));
    const colIdxs = columnKeys.map((k) => colIndexByKey.get(k));
    // Même validité qu'au rendu (computeMergedCellLayout).
    if (rowIdxs.some((i) => i === undefined) || colIdxs.some((i) => i === undefined)) continue;
    if (!isContiguous(rowIdxs) || !isContiguous(colIdxs)) continue;
    return [...rowKeys].sort((a, b) => rowIndexByKey.get(a) - rowIndexByKey.get(b));
  }
  return [rowKey];
};

// Vide au sens le plus simple : null, chaîne blanche, valeur fausse. Le
// document a besoin d'une définition plus large — une cellule « vide » y est
// souvent un état Draft sérialisé — d'où le paramètre `isEmptyValue` de
// `computeLabelRowMerges`, que le site n'a pas à connaître.
const isBlankValue = (value) => {
  if (value == null) return true;
  if (typeof value !== 'string') return !value;
  return value.trim() === '';
};

const plainText = (value) => (value == null ? '' : String(value)).trim();

/**
 * Une ligne est-elle candidate à la fusion auto, et comment ?
 * - `{ kind: 'empty' }` : ligne entièrement vide, le séparateur que les
 *   services émettent ; sans fusion, on y voyait les filets de colonnes ;
 * - `{ kind: 'label', index, end, filled, label, alone }` : un libellé (1ʳᵉ cellule
 *   remplie) suivi d'au moins une cellule vide. La fusion va de `index` à
 *   `end`, la dernière cellule vide avant la prochaine remplie : rien n'est
 *   jamais masqué. `alone` : le libellé est la seule cellule remplie — une
 *   ligne-titre (« MASSE SALARIALE », « Matériel ») ; sinon une ligne de
 *   total dont le libellé s'étale jusqu'au montant (« TOTAL TTC »). `filled` :
 *   indices des cellules remplies ; `rowKind` : type donné par l'API (zone) ;
 * - `null` : ligne de données, jamais fusionnée.
 * `getText` donne le texte nu du libellé (le document stocke du Draft).
 */
export const getLabelRowCandidate = (
  row,
  columns,
  isEmptyValue = isBlankValue,
  getText = plainText
) => {
  const filled = [];
  columns.forEach((col, idx) => {
    if (!isEmptyValue(row[col.key])) filled.push(idx);
  });
  if (filled.length === 0) return { kind: 'empty' };
  const [index, next = columns.length] = filled;
  const end = next - 1;
  // Pas de cellule vide juste après le libellé : rien à étaler.
  if (end <= index) return null;
  return {
    kind: 'label',
    index,
    end,
    filled,
    label: getText(row[columns[index].key]),
    alone: filled.length === 1,
    rowKind: row.rowKind,
  };
};

/**
 * Fusion d'une ligne candidate selon les réglages `tableConfig.merge` :
 * - `labelRows` / `emptyRows` (vrais par défaut) : règle générale. Par
 *   défaut, seule la ligne-titre dont le libellé est en 1ʳᵉ colonne fusionne ;
 *   une ligne de total ne fusionne que sur exception ;
 * - `overrides` : exceptions par libellé `{ label, merge }`, choisies au
 *   canvas. Par libellé et non par ligne : les clés de lignes changent à
 *   chaque rafraîchissement, le libellé non. L'exception vaut donc pour toutes
 *   les lignes qui portent ce libellé. Une règle `{ kind, merge }` vaut pour
 *   toutes les lignes de ce type (`rowKind`, ex. les zones, dont le libellé
 *   change d'une affaire à l'autre) et passe avant celle du libellé. Les lignes
 *   vides ont leur propre type, `{ kind: 'empty' }`, déduit de la ligne : elles
 *   servent d'espaces, un seul réglage suffit pour toutes.
 * @returns {{ merge: boolean, defaultMerge: boolean, override }}
 */
export const resolveLabelRowMerge = (candidate, options) => {
  const { labelRows = true, emptyRows = true, overrides = [] } = options ?? {};
  if (!candidate) return { merge: false, defaultMerge: false };
  if (candidate.kind === 'empty') {
    // Une règle `{ kind: 'empty' }` vaut pour toutes les lignes vides et passe
    // avant l'interrupteur général.
    const override = overrides.find((o) => o.kind === 'empty');
    return { merge: override ? !!override.merge : emptyRows, defaultMerge: emptyRows, override };
  }
  const defaultMerge = candidate.alone && candidate.index === 0 && labelRows;
  const kindRule = candidate.rowKind
    ? overrides.find((o) => o.kind === candidate.rowKind)
    : undefined;
  const override = kindRule ?? (candidate.label
    ? overrides.find((o) => !o.kind && o.label === candidate.label)
    : undefined);
  return { merge: override ? !!override.merge : defaultMerge, defaultMerge, override };
};

/**
 * Colonnes couvertes par la fusion d'une ligne à libellé, en indices inclus.
 * Par défaut, de la 1ʳᵉ colonne à la dernière cellule vide avant la prochaine
 * remplie. Une exception peut fixer ses bornes, `from` / `to`, par libellé
 * d'origine de colonne (`columnLabel`) : stable au réordonnancement et au
 * renommage. Une borne introuvable (colonne masquée) reprend sa valeur par
 * défaut. null si la plage recouvrirait plus d'une cellule remplie — le texte
 * des autres disparaîtrait — ou ne couvre qu'une colonne.
 * @returns {{ start, end, contentIndex }|null} contentIndex : cellule dont le
 *   texte remplit la fusion, null s'il n'y en a pas.
 */
const labelRowRange = (candidate, columns, override, columnLabel) => {
  const indexOf = (label) => (label == null ? -1 : columns.findIndex((col) => columnLabel(col) === label));
  const from = indexOf(override?.from);
  const to = indexOf(override?.to);
  let start = from >= 0 ? from : 0;
  let end = to >= 0 ? to : candidate.end;
  if (start > end) [start, end] = [end, start];
  if (end <= start) return null;
  const inside = candidate.filled.filter((idx) => idx >= start && idx <= end);
  if (inside.length > 1) return null;
  return { start, end, contentIndex: inside.length ? inside[0] : null };
};

const defaultColumnLabel = (col) => col.label ?? col.key;

/**
 * Fusion auto des lignes-titre et des lignes vides d'un tableau variable
 * (cf. getLabelRowCandidate / resolveLabelRowMerge). Retourne des entrées
 * mergedCells, à combiner avec les fusions existantes. Recalculé au rendu :
 * survit à la régénération des clés de lignes au refetch.
 * Une ligne ajoutée à la main (`manual`) n'est jamais fusionnée : elle naît
 * vide et serait sinon fusionnée aussitôt, impossible à remplir.
 * `options` : réglages `tableConfig.merge`, plus `getText` (texte nu) et
 * `columnLabel` (libellé d'origine d'une colonne, cf. labelRowRange).
 */
export const computeLabelRowMerges = (data, columns, isEmptyValue = isBlankValue, options) => {
  if (!Array.isArray(data) || !Array.isArray(columns) || columns.length < 2) {
    return [];
  }
  const merges = [];
  data.forEach((row) => {
    if (row.manual) return;
    const candidate = getLabelRowCandidate(row, columns, isEmptyValue, options?.getText);
    const { merge, override } = resolveLabelRowMerge(candidate, options);
    if (!merge) return;
    const columnLabel = options?.columnLabel ?? defaultColumnLabel;
    if (candidate.kind === 'empty') {
      // Rien à masquer dans une ligne vide : toute plage est valide. Sans règle,
      // elle s'étale sur toutes les colonnes.
      const span = labelRowRange({ filled: [], end: columns.length - 1 }, columns, override, columnLabel);
      if (span) {
        merges.push({
          rowKeys: [row.key],
          columnKeys: columns.slice(span.start, span.end + 1).map((col) => col.key),
        });
      }
      return;
    }
    const range = labelRowRange(candidate, columns, override, columnLabel);
    if (!range) return;
    // L'ancre est la 1ʳᵉ colonne de la plage : si elle est vide (« TOTAL TTC »
    // sous « Intitulé », fusion depuis « Référence »), `contentKey` dit d'où
    // vient le texte de la cellule fusionnée.
    const { start, end, contentIndex } = range;
    merges.push({
      rowKeys: [row.key],
      columnKeys: columns.slice(start, end + 1).map((col) => col.key),
      ...(contentIndex != null && contentIndex !== start ? { contentKey: columns[contentIndex].key } : {}),
    });
  });
  return merges;
};

/**
 * État de fusion d'une ligne à libellé (ligne-titre ou de total), pour le
 * panneau de droite et l'aperçu du Drawer : son libellé, si elle est
 * fusionnée, et si elle le serait sans exception. Une ligne vide n'a pas de
 * libellé, seulement son type (`kind: 'empty'`). null pour une ligne de
 * données ou ajoutée à la main.
 */
export const getLabelRowMergeState = (row, columns, isEmptyValue = isBlankValue, options) => {
  if (!row || row.manual || !Array.isArray(columns) || columns.length < 2) return null;
  const candidate = getLabelRowCandidate(row, columns, isEmptyValue, options?.getText);
  if (candidate?.kind === 'empty') {
    const { merge, defaultMerge } = resolveLabelRowMerge(candidate, options);
    return { label: '', kind: 'empty', merged: merge, defaultMerged: defaultMerge };
  }
  if (candidate?.kind !== 'label' || !candidate.label) return null;
  const { merge, defaultMerge } = resolveLabelRowMerge(candidate, options);
  return {
    label: candidate.label,
    ...(candidate.rowKind ? { kind: candidate.rowKind } : {}),
    merged: merge,
    defaultMerged: defaultMerge,
  };
};

/** Identité d'une règle de fusion : son type de ligne, sinon son libellé. */
export const mergeRuleKey = (rule) => (rule.kind ? `kind:${rule.kind}` : `label:${rule.label}`);

const ruleOf = ({ label, kind }) => (kind ? { kind } : { label });
const withoutRule = (overrides, rule) =>
  (overrides || []).filter((o) => mergeRuleKey(o) !== mergeRuleKey(rule));

/**
 * Fusion de la ligne `state` (cf. getLabelRowMergeState) de la colonne `from`
 * à la colonne `to`, par libellé d'origine : la règle remplace la précédente
 * du même libellé ou du même type.
 */
export const mergeLabelRowRange = (overrides, state, { from, to }) => {
  const rule = ruleOf(state);
  return [...withoutRule(overrides, rule), { ...rule, merge: true, from, to }];
};

/** Défusionne la ligne `state` : sans exception si c'est déjà la règle générale. */
export const unmergeLabelRow = (overrides, state) => {
  const rule = ruleOf(state);
  const rest = withoutRule(overrides, rule);
  return state.defaultMerged ? [...rest, { ...rule, merge: false }] : rest;
};

/**
 * Plage de cellules fusionnable dans une ligne : au moins deux cellules, et au
 * plus une remplie — le texte des autres disparaîtrait sous la fusion.
 * `filled(index)` dit si la cellule d'indice `index` porte un contenu.
 */
export const canMergeRowRange = (c0, c1, filled) => {
  if (c1 <= c0) return false;
  let count = 0;
  for (let idx = c0; idx <= c1; idx++) if (filled(idx)) count++;
  return count <= 1;
};

/**
 * Exceptions après bascule de la ligne `label` — ou de toutes celles de son
 * type `kind` : on n'en garde une que si elle s'écarte de la règle générale,
 * sinon la liste grossirait d'entrées sans effet.
 */
export const toggleLabelRowOverride = (overrides, { label, kind, merged, defaultMerged }) => {
  const rule = ruleOf({ label, kind });
  const rest = withoutRule(overrides, rule);
  const next = !merged;
  return next === defaultMerged ? rest : [...rest, { ...rule, merge: next }];
};

/**
 * Fusion verticale des valeurs répétées, pour les colonnes que l'API marque
 * `merge` : deux lignes voisines portant la même valeur ne forment qu'une
 * cellule. C'est ce que le document obtient par `hiddenColumnsKeys`
 * (`utils/dataSync.js`) ; exprimé ici en fusions, le rendu est le même des deux
 * côtés sans code de rendu supplémentaire.
 *
 * @param {object[]} data      lignes, avec leur `key`
 * @param {object[]} columns   colonnes, avec leur `key` et leur drapeau `merge`
 * @param {(row, column) => any} getValue  valeur d'une cellule
 */
export const computeRepeatedValueMerges = (data, columns, getValue) => {
  if (!Array.isArray(data) || !Array.isArray(columns)) return [];
  const merges = [];

  columns.filter((column) => column?.merge).forEach((column) => {
    let run = [];
    let runValue;

    const flush = () => {
      if (run.length > 1) merges.push({ rowKeys: run, columnKeys: [column.key] });
      run = [];
    };

    data.forEach((row) => {
      const value = getValue(row, column);
      // Une valeur vide ne fusionne rien : sinon toutes les cases vides d'une
      // colonne n'en formeraient qu'une.
      if (value == null || value === '' ) {
        flush();
        runValue = undefined;
        return;
      }
      if (run.length && value === runValue) {
        run.push(row.key);
        return;
      }
      flush();
      run = [row.key];
      runValue = value;
    });
    flush();
  });

  return merges;
};

/**
 * Convertit un rectangle d'INDICES { r0, r1, c0, c1 } (bornes incluses, ordre
 * courant) en clés { rowKeys, columnKeys }. On travaille par indices pour la
 * sélection car les clés de lignes peuvent être dupliquées (ex. lignes ajoutées
 * / dupliquées) — un matching par clé ferait fuiter la sélection sur des lignes
 * homonymes. La conversion en clés n'a lieu qu'au moment de fusionner.
 */
export const rectToKeys = (data, columns, rect) => {
  if (!rect) return null;
  const r0 = Math.max(0, Math.min(rect.r0, rect.r1));
  const r1 = Math.min(data.length - 1, Math.max(rect.r0, rect.r1));
  const c0 = Math.max(0, Math.min(rect.c0, rect.c1));
  const c1 = Math.min(columns.length - 1, Math.max(rect.c0, rect.c1));
  return {
    rowKeys: data.slice(r0, r1 + 1).map((row) => row.key),
    columnKeys: columns.slice(c0, c1 + 1).map((col) => col.key),
  };
};

/**
 * Le geste qui étend une sélection (cases à fusionner, colonnes) : Maj + clic.
 * Le même pour le tableau du document, l'aperçu de configuration et le canvas
 * du web-editor : Ctrl / ⌘ + clic n'en est pas un.
 */
export const isMultiSelectClick = (event) => !!event?.shiftKey;

// Indices [premier, dernier] des éléments dont la clé est donnée, `null` s'il
// n'y en a aucun ou s'ils ne se suivent pas (sélection par bascule).
const contiguousKeyRange = (items, keys) => {
  const keySet = new Set(keys);
  const indices = items.flatMap((item, i) => (keySet.has(item.key) ? [i] : []));
  if (indices.length === 0) return null;
  const first = indices[0];
  const last = indices[indices.length - 1];
  return indices.length === last - first + 1 ? [first, last] : null;
};

/**
 * Rectangle d'INDICES couvrant toutes les cases des lignes données (sélection
 * par numéro de ligne), c'est-à-dire ce qu'un shift+clic de la 1re à la
 * dernière case de ces lignes sélectionnerait. `null` si une fusion n'a pas de
 * sens : aucune ligne, ou lignes non contiguës (sélection par bascule).
 */
export const rowKeysToRect = (data, rowKeys, columnCount) => {
  const range = contiguousKeyRange(data, rowKeys);
  if (!range || columnCount < 1) return null;
  return { r0: range[0], r1: range[1], c0: 0, c1: columnCount - 1 };
};

/**
 * Pendant de rowKeysToRect pour les colonnes sélectionnées par leur poignée
 * d'en-tête : toutes leurs cases, de la première à la dernière ligne.
 */
export const columnKeysToRect = (columns, columnKeys, rowCount) => {
  const range = contiguousKeyRange(columns, columnKeys);
  if (!range || rowCount < 1) return null;
  return { r0: 0, r1: rowCount - 1, c0: range[0], c1: range[1] };
};

/** Une fusion existante intersecte-t-elle le rectangle sélectionné ? */
export const mergeIntersectsSelection = (merge, selection) => {
  if (!selection) return false;
  const rowSet = new Set(selection.rowKeys);
  const colSet = new Set(selection.columnKeys);
  return (merge.rowKeys ?? []).some((rk) => rowSet.has(rk)) &&
    (merge.columnKeys ?? []).some((ck) => colSet.has(ck));
};

/**
 * Résout l'ancre (case haut-gauche) d'une fusion dans l'ordre courant.
 * @returns {{ rowKey, columnKey } | null}
 */
export const resolveMergeAnchor = (data, columns, merge) => {
  const rowIdxByKey = new Map(data.map((r, i) => [r.key, i]));
  const colIdxByKey = new Map(columns.map((c, i) => [c.key, i]));
  const rowIdxs = (merge.rowKeys ?? []).map((k) => rowIdxByKey.get(k)).filter((i) => i !== undefined);
  const colIdxs = (merge.columnKeys ?? []).map((k) => colIdxByKey.get(k)).filter((i) => i !== undefined);
  if (rowIdxs.length === 0 || colIdxs.length === 0) return null;
  return {
    rowKey: data[Math.min(...rowIdxs)].key,
    columnKey: columns[Math.min(...colIdxs)].key,
  };
};

/** Sélection fusionnable : ≥2 cases et aucune fusion existante ne la recoupe. */
export const canMergeSelection = (selection, mergedCells) => {
  if (!selection) return false;
  const count = (selection.rowKeys?.length ?? 0) * (selection.columnKeys?.length ?? 0);
  if (count < 2) return false;
  return !(mergedCells ?? []).some((m) => mergeIntersectsSelection(m, selection));
};

/** Y a-t-il une fusion existante à défusionner sous la sélection ? */
export const canUnmergeSelection = (selection, mergedCells) => {
  if (!selection || !Array.isArray(mergedCells) || mergedCells.length === 0) return false;
  return mergedCells.some((m) => mergeIntersectsSelection(m, selection));
};

/**
 * La sélection ne couvre-t-elle que des lignes ajoutées à la main (`manual`) ?
 * Seules celles-là sont fusionnables dans un tableau variable : les lignes de
 * l'API sont remplacées, clés comprises, à chaque rafraîchissement, et leur
 * fusion serait perdue avec le contenu déplacé dans l'ancre.
 */
export const selectionHasOnlyManualRows = (data, selection) => {
  if (!selection || !Array.isArray(data)) return false;
  const rowSet = new Set(selection.rowKeys ?? []);
  const rows = data.filter((row) => rowSet.has(row.key));
  return rows.length > 0 && rows.every((row) => row.manual);
};

/** Retourne mergedCells sans les fusions recoupées par la sélection. */
export const removeMergesInSelection = (mergedCells, selection) =>
  (mergedCells ?? []).filter((m) => !mergeIntersectsSelection(m, selection));

/* ------------------------------------------------------------------------ */
/* Groupes d'en-têtes (sous-headers)                                          */
/*                                                                            */
/* Un tableau variable reçoit sa hiérarchie du back (children dans            */
/* columns_informations) ; ces fonctions laissent composer la même structure  */
/* à la main. Un seul niveau : `children` ne contient que des feuilles.       */
/* ------------------------------------------------------------------------ */

/** Indices, dans l'ordre de `columns`, des colonnes sélectionnées. */
const selectedColumnIndexes = (selectedKeys, columns) => {
  const keySet = new Set(selectedKeys ?? []);
  return (columns ?? [])
    .map((col, idx) => (keySet.has(col.key) ? idx : -1))
    .filter((idx) => idx !== -1);
};

/** Clés des colonnes déjà rangées sous un parent. */
const groupedLeafKeys = (columnsDisposition) => {
  const keys = new Set();
  (columnsDisposition ?? []).forEach((col) => {
    if (col.children) col.children.forEach((child) => keys.add(child.key));
  });
  return keys;
};

/**
 * Sélection groupable : au moins 2 colonnes, contiguës, et aucune déjà dans un
 * groupe. La contiguïté n'est pas un caprice — un en-tête de groupe est une
 * cellule à colSpan, elle ne peut couvrir qu'un bloc continu. Même règle que
 * la fusion de cellules, et rien n'entre dans un groupe sans avoir été cliqué.
 */
export const canGroupColumns = (selectedKeys, columns, columnsDisposition) => {
  const idxs = selectedColumnIndexes(selectedKeys, columns);
  if (idxs.length < 2) return false;
  if (idxs[idxs.length - 1] - idxs[0] !== idxs.length - 1) return false;
  const grouped = groupedLeafKeys(columnsDisposition);
  return !idxs.some((idx) => grouped.has(columns[idx].key));
};

/** Y a-t-il un groupe à défaire sous la sélection ? */
export const canUngroupColumns = (selectedKeys, columnsDisposition) => {
  if (!Array.isArray(columnsDisposition) || columnsDisposition.length === 0) return false;
  const keySet = new Set(selectedKeys ?? []);
  return columnsDisposition.some(
    (col) => col.children && col.children.some((child) => keySet.has(child.key))
  );
};

/**
 * Enveloppe les colonnes sélectionnées sous un nouveau parent.
 *
 * Amorce la disposition en miroir plat de `columns` quand elle est vide : sans
 * ça getLeafColumnsCount ne couvrirait pas toutes les feuilles et TableHead
 * ignorerait la hiérarchie (cf. son garde `hasDisposition`).
 */
export const groupColumns = (columnsDisposition, columns, selectedKeys, title) => {
  if (!canGroupColumns(selectedKeys, columns, columnsDisposition)) return columnsDisposition;
  const base =
    Array.isArray(columnsDisposition) && columnsDisposition.length > 0
      ? columnsDisposition
      : (columns ?? []).map((col) => ({ key: col.key, title: col.title, width: col.width }));

  const keySet = new Set(selectedKeys);
  const firstIdx = base.findIndex((col) => !col.children && keySet.has(col.key));
  if (firstIdx === -1) return columnsDisposition;
  const picked = base.filter((col) => !col.children && keySet.has(col.key));

  const next = base.filter((col) => col.children || !keySet.has(col.key));
  next.splice(firstIdx, 0, { key: colId(), title, children: picked });
  return next;
};

/**
 * Défait les groupes recoupés par la sélection : leurs enfants remontent à la
 * racine, à la place du parent. Si plus aucun groupe ne subsiste, retourne []
 * pour que TableHead retombe sur le rendu plat plutôt que de garder une
 * disposition qui ne décrit plus rien.
 */
export const ungroupColumns = (columnsDisposition, selectedKeys) => {
  if (!canUngroupColumns(selectedKeys, columnsDisposition)) return columnsDisposition;
  const keySet = new Set(selectedKeys ?? []);
  const next = columnsDisposition.flatMap((col) => {
    if (col.children && col.children.some((child) => keySet.has(child.key))) {
      return col.children;
    }
    return [col];
  });
  return next.some((col) => col.children) ? next : [];
};
